import { z } from "zod";
import {
  EventOwnerViewSchema,
  EventUserViewSchema,
  type EventAdminView,
  type EventOwnerView,
  type EventUserView,
} from "./event.schemas.js";

/**
 * The event as an authenticated read returns it — one of three role tiers.
 *
 * `EventPublicView` is deliberately NOT a member: it is served by a separate
 * anonymous endpoint, so the authenticated path cannot produce it.
 */
export type EventViewUnion = EventAdminView | EventOwnerView | EventUserView;

/** The by-id endpoint returns the standard role-tier views. */
export const EventViewUnionSchema = z.union([
  EventOwnerViewSchema,
  EventUserViewSchema,
]);

/** Counts belong to the events-list response, not the general owner view. */
export const MyEventOwnerListItemSchema = z.intersection(
  EventOwnerViewSchema,
  z.object({
    participantCounts: z
      .object({ notJoined: z.number(), joined: z.number() })
      .readonly(),
  }),
);
export type MyEventOwnerListItem = z.infer<typeof MyEventOwnerListItemSchema>;

/**
 * MyEventViewUnionSchema - Response item shape for GET /api/v1/me/events.
 *
 * Heterogeneous: owner-tier when the actor created the event, user-tier when
 * they're only a participant. Owner is first in the union so owner-shaped
 * payloads match the richer schema and never get silently downcast to the
 * leaner user schema (which would otherwise accept them since the user
 * schema has no required host-metadata fields).
 *
 * Discriminate at consumers via `data.$roles.includes('event:owner')`.
 */
export const MyEventViewUnionSchema = z.union([
  MyEventOwnerListItemSchema,
  EventUserViewSchema,
]);

export type MyEventViewUnion = z.infer<typeof MyEventViewUnionSchema>;
